import { useState, useRef, useEffect, useCallback } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { cn } from '@/lib/utils';
import { toolStateTone } from '@/lib/toolStateTone';
import { useTranslation } from '@/hooks/useTranslation';
import { useLabeling } from '../../context/useLabeling';
import type { ImageLabel, ToolImage } from '../../types/LabelingTypes';
import { formatCapturedAt } from '../../utils/formatCapturedAt';

const STATE_SHORT_LABEL: Record<ImageLabel, string> = {
  ok: 'labeling.state.shortOk',
  worn: 'labeling.state.shortWorn',
  unreadable: 'labeling.state.shortUnreadable',
};

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.25;

function clampScale(s: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, parseFloat(s.toFixed(2))));
}

interface ImageViewerProps {
  /** Image to display. Defaults to the labeling context's `current`. */
  image?: ToolImage;
  /** Position shown in the footer. Defaults to the image's index in `filtered`. */
  position?: { index: number; total: number };
  /** Whether the label chip is shown for a labeled image. Defaults to `true`. */
  showLabelChip?: boolean;
}

export function ImageViewer({ image, position, showLabelChip = true }: ImageViewerProps = {}) {
  const { t } = useTranslation();
  const { current, filtered, refreshImageUrl } = useLabeling();

  const effectiveImage = image ?? current;

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const viewportRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [effectiveImage?.key]);

  const zoom = useCallback((delta: number) => {
    setScale((prev) => {
      const next = clampScale(prev + delta);
      if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const handleFit = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      zoom(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    },
    [zoom]
  );

  const handlePanDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (scale > 1) {
        isDraggingRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    },
    [scale]
  );

  const handlePanMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;
      setOffset((prev) => {
        const vp = viewportRef.current;
        if (!vp) return prev;
        const maxX = (vp.clientWidth * (scale - 1)) / 2;
        const maxY = (vp.clientHeight * (scale - 1)) / 2;
        return {
          x: Math.max(-maxX, Math.min(maxX, prev.x + e.movementX)),
          y: Math.max(-maxY, Math.min(maxY, prev.y + e.movementY)),
        };
      });
    },
    [scale]
  );

  const handlePanUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  if (!effectiveImage) return null;

  const label = showLabelChip ? effectiveImage.label : null;
  const idx = filtered.findIndex((img) => img.key === effectiveImage.key);
  const displayIndex = idx >= 0 ? idx : 0;
  const effectivePosition = position ?? { index: displayIndex, total: filtered.length };

  return (
    <div className="flex-1 w-full flex flex-col rounded-2xl border border-border bg-card overflow-hidden">
      {/* Top bar: zoom controls + label chip */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <div className="flex items-center gap-1">
          <IconButton
            icon={Minus}
            variant="outline"
            label={t('labeling.viewer.zoomOut')}
            onClick={() => zoom(-ZOOM_STEP)}
          />
          <IconButton
            icon={Plus}
            variant="outline"
            label={t('labeling.viewer.zoomIn')}
            onClick={() => zoom(ZOOM_STEP)}
          />
          <Button variant="outline" size="sm" onClick={handleFit}>
            {t('labeling.viewer.zoomFit')}
          </Button>
        </div>
        {label && <Badge tone={toolStateTone(label)}>{t(STATE_SHORT_LABEL[label])}</Badge>}
      </div>

      <div
        ref={viewportRef}
        className={cn(
          'relative flex-1 bg-zinc-950 overflow-hidden select-none',
          scale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
        )}
        style={{ touchAction: 'none' }}
        onPointerDown={handlePanDown}
        onPointerMove={handlePanMove}
        onPointerUp={handlePanUp}
        onPointerCancel={handlePanUp}
        onWheel={handleWheel}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={effectiveImage.imageUrl}
            alt={t('labeling.viewer.imageAlt')}
            draggable={false}
            onError={() => void refreshImageUrl(effectiveImage.key)}
            className="max-w-full max-h-full object-contain"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transformOrigin: 'center',
            }}
          />
        </div>
      </div>

      <div className="flex justify-between text-sm text-muted-foreground px-3 py-2 border-t border-border">
        <span>
          {t('labeling.viewer.capturedAt', {
            datetime: formatCapturedAt(effectiveImage.capturedAt),
          })}
        </span>
        <span>
          {t('labeling.viewer.imageCount', {
            n: effectivePosition.index + 1,
            total: effectivePosition.total,
          })}
        </span>
      </div>
    </div>
  );
}
